import {
  Alert,
  Button,
  Group,
  LoadingOverlay,
  Modal,
  Stack,
  Text,
  useComputedColorScheme,
} from '@mantine/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useGetClaim } from 'api/resource';
import { Claim, Payor, Provider } from 'interfaces';
import { ClaimForm } from '../ModalComponents';
import { ClaimFormProvider } from '../ClaimFormProvider';
import { ClaimEditModal } from '../ModalComponents/ClaimEditModal';
import { useClaimEditStepperStore } from 'store/ClaimEditStepperStore';
import { useClaimHandlers } from '../ClaimManagementComponents/useClaimHandlers';
import { buttonTextColor } from 'utils/buttonUtils';
import {
  createRow,
  MRT_EditCellTextInput,
  MRT_Row,
  MRT_TableInstance,
} from 'mantine-react-table';
import React from 'react';
import { useLiveClaimRules } from 'hooks/useLiveClaimRules';

import * as Sentry from '@sentry/react';

interface ClaimBatchReviewModalProps {
  open: boolean;
  onClose: () => void;
  claimIds: string[];
  providers: Provider[];
  payors: Payor[];
  table: MRT_TableInstance<Claim>;
  internalEditComponents?: React.ReactNode[];
}

const getDefaultDirtyFields = () => ({
  patient: false,
  provider: false,
  tariffs: false,
  status: false,
  site: false,
  healthcard: false,
  debits: false,
  credits: false,
});

const ClaimBatchReviewContent = ({
  row,
  providers,
  payors,
  dirtyFieldsRef,
  onSave,
  onNext,
  onFinish,
  onCancel,
  internalEditComponents,
}: {
  row: MRT_Row<Claim>;
  providers: Provider[];
  payors: Payor[];
  dirtyFieldsRef: React.MutableRefObject<Record<string, boolean>>;
  onSave: (args: {
    values: Claim;
    dirtyFields: Record<string, boolean>;
  }) => void;
  onNext: () => void;
  onFinish: () => void;
  onCancel: () => void;
  internalEditComponents: React.ReactNode[];
}) => {
  const liveRules = useLiveClaimRules({ claimID: row.original.id });
  const editForm = ClaimForm({
    row,
    onValuesChange: liveRules.scheduleEvaluation,
  });
  useEffect(() => {
    liveRules.scheduleEvaluation(editForm.getValues());
  }, []);
  return (
    <ClaimFormProvider
      form={{
        formType: 'edit',
        editForm,
        createForm: undefined,
      }}
    >
      <ClaimEditModal
        table={
          {
            setEditingRow: () => undefined,
            resetExpanded: () => undefined,
            options: {},
          } as unknown as MRT_TableInstance<Claim>
        }
        row={row}
        internalEditComponents={internalEditComponents}
        dirtyFields={dirtyFieldsRef}
        providers={providers}
        payors={payors}
        deferSave
        liveRules={liveRules}
        onDeferredSave={({ values, dirtyFields }) =>
          onSave({ values, dirtyFields })
        }
        onDeferredNext={onNext}
        onDeferredFinish={onFinish}
        onDeferredCancel={onCancel}
      />
    </ClaimFormProvider>
  );
};

export const ClaimBatchReviewModal = ({
  open,
  onClose,
  claimIds,
  providers,
  payors,
  table,
  internalEditComponents,
}: ClaimBatchReviewModalProps) => {
  const colourMode = useComputedColorScheme();
  const setQueue = useClaimEditStepperStore((state) => state.setQueue);
  const queue = useClaimEditStepperStore((state) => state.queue);
  const currentIndex = useClaimEditStepperStore((state) => state.currentIndex);
  const setCurrentById = useClaimEditStepperStore(
    (state) => state.setCurrentById
  );
  const [nextButtonColour, setNextButtonColour] = useState<string>('blue');
  const stepperNext = useClaimEditStepperStore((state) => state.next);
  const stepperPrevious = useClaimEditStepperStore((state) => state.previous);
  const clearQueue = useClaimEditStepperStore((state) => state.clear);
  const [stagedChanges, setStagedChanges] = useState<
    Record<
      string,
      {
        values: Claim;
        dirtyFields: Record<string, boolean>;
      }
    >
  >({});
  const [isApplying, setIsApplying] = useState(false);

  const activeClaimId = queue[currentIndex] ?? claimIds[0] ?? null;
  const { data: claimData, isLoading: claimLoading } = useGetClaim({
    id: activeClaimId,
    displayNotification: false,
  });
  useEffect(() => {
    if (open && claimIds.length) {
      setQueue(claimIds);
    } else if (!open) {
      clearQueue();
      setStagedChanges({});
    }
  }, [open, claimIds, setQueue, clearQueue]);

  useEffect(() => {
    if (activeClaimId) {
      setCurrentById(activeClaimId);
    }
  }, [activeClaimId, setCurrentById]);

  const dirtyFieldsRef = useRef<Record<string, boolean>>(
    getDefaultDirtyFields()
  );

  useEffect(() => {
    dirtyFieldsRef.current = getDefaultDirtyFields();
  }, [activeClaimId]);

  const rowStub = useMemo(() => {
    if (!claimData) return null;
    const row = createRow(table, claimData, currentIndex);
    row.id = claimData.id;
    row.original = claimData;
    // eslint-disable-next-line
    // @ts-ignore
    row._valuesCache = claimData;
    return row;
  }, [claimData, table, currentIndex]);

  const effectiveInternalEditComponents = useMemo(() => {
    if (internalEditComponents && internalEditComponents.length) {
      return internalEditComponents;
    }
    if (!rowStub) return [];
    return rowStub
      .getAllCells()
      .filter((cell) => cell.column.columnDef.columnDefType === 'data')
      .map((cell) => (
        <MRT_EditCellTextInput cell={cell} key={cell.id} table={table} />
      ));
  }, [internalEditComponents, rowStub, table]);

  const { handleUpdateClaim } = useClaimHandlers({
    dirtyFields: dirtyFieldsRef,
    resetDirtyFields: () => {
      dirtyFieldsRef.current = getDefaultDirtyFields();
    },
  });

  const stageClaim = async ({
    claimId,
    values,
    dirtyFields,
  }: {
    claimId: string;
    values: Claim;
    dirtyFields: Record<string, boolean>;
  }) => {
    setStagedChanges((prev) => ({
      ...prev,
      [claimId]: { values, dirtyFields },
    }));
  };

  const goNext = () => {
    const nextId = stepperNext();
    if (nextId) {
      setCurrentById(nextId);
    }
  };

  const goPrevious = () => {
    const prevId = stepperPrevious();
    if (prevId) {
      setCurrentById(prevId);
    }
  };

  const applyAllChanges = async () => {
    if (!queue.length) {
      onClose();
      return;
    }
    setIsApplying(true);
    try {
      for (const id of queue) {
        const staged = stagedChanges[id];
        if (!staged) continue;
        dirtyFieldsRef.current = staged.dirtyFields || getDefaultDirtyFields();
        await handleUpdateClaim({
          values: staged.values,
          row: { original: staged.values } as MRT_Row<Claim>,
          table,
        });
      }
      clearQueue();
      setStagedChanges({});
      onClose();
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setIsApplying(false);
    }
  };

  const hasNoStagedChanges = queue.every((id) => !stagedChanges[id]);

  return (
    <Modal
      opened={open}
      onClose={async () => {
        await applyAllChanges();
        clearQueue();
        setStagedChanges({});
        onClose();
      }}
      size="90vw"
      title="Review Errored Claims"
      radius="md"
    >
      <Stack gap="md" style={{ position: 'relative' }}>
        <LoadingOverlay visible={isApplying} />
        <Group justify="space-between">
          <Text>
            Claim {queue.length ? currentIndex + 1 : 0} of {queue.length || 0}
          </Text>
          <Group>
            <Button
              onClick={goPrevious}
              color="blue"
              c={buttonTextColor(colourMode, currentIndex <= 0)}
              disabled={currentIndex <= 0}
            >
              Previous
            </Button>
            <Button
              onClick={goNext}
              color={nextButtonColour}
              c={buttonTextColor(colourMode, currentIndex >= queue.length - 1)}
              disabled={currentIndex >= queue.length - 1}
            >
              {nextButtonColour === 'green' ? 'Next' : 'Skip'}
            </Button>
            {currentIndex >= queue.length - 1 && (
              <Button
                color="green"
                c={buttonTextColor(colourMode, hasNoStagedChanges)}
                disabled={hasNoStagedChanges}
                loading={isApplying}
                onClick={applyAllChanges}
              >
                Apply All Changes
              </Button>
            )}
          </Group>
        </Group>
        {claimLoading || !claimData || !rowStub ? (
          <Alert color="blue" variant="light">
            {claimLoading ? 'Loading claim...' : 'No claim selected.'}
          </Alert>
        ) : (
          <ClaimBatchReviewContent
            key={claimData.id}
            row={rowStub}
            providers={providers}
            payors={payors}
            dirtyFieldsRef={dirtyFieldsRef}
            internalEditComponents={effectiveInternalEditComponents}
            onSave={({ values, dirtyFields: dirty }) => {
              stageClaim({
                claimId: claimData.id,
                values,
                dirtyFields: dirty,
              });
              setNextButtonColour('green');
            }}
            onNext={goNext}
            onFinish={applyAllChanges}
            onCancel={() => {
              clearQueue();
              onClose();
            }}
          />
        )}
      </Stack>
    </Modal>
  );
};
